import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import {
  aplicarValidadoresConexao,
  CAMPOS_CONEXAO,
  controlesConexao,
  credencialPadrao,
  montarConfig,
  montarCredenciais,
  valoresPadraoConexao
} from '../../../../components/smartinteg/conexao-campos/conexao-form.util';
import {
  TipoProvedor,
  Provedor,
  CreateProvedorCompleto
} from '../../../../types/smartinteg';

@Component({
  selector: 'app-provedor-form',
  templateUrl: './provedor-form.component.html',
  styleUrl: './provedor-form.component.css'
})
export class ProvedorFormComponent implements OnInit {
  form!: FormGroup;
  loading: boolean = false;
  isEditMode: boolean = false;
  provedorId?: string;
  private provedorCarregado?: Provedor;

  tiposProvedor = [
    { label: 'Banco de Dados', value: TipoProvedor.BASEDADOS },
    { label: 'API REST', value: TipoProvedor.APIREST }
  ];

  constructor(
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();

    this.form.get('tipo')?.valueChanges.subscribe((tipo: TipoProvedor) => {
      this.form.patchValue({ tipoCredencial: credencialPadrao(tipo) });
      this.aplicarValidadores();
    });
    this.form.get('tipoCredencial')?.valueChanges.subscribe(() => this.aplicarValidadores());

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.configurarModoEdicao(id);
    }
  }

  get tipoProvedor(): TipoProvedor {
    return this.form.get('tipo')?.value;
  }

  inicializarFormulario(): void {
    this.form = this.fb.group({
      // Provedor
      nome: ['', [Validators.required, Validators.minLength(3)]],
      descricao: [''],
      tipo: [TipoProvedor.BASEDADOS, Validators.required],

      // Primeira conexão (campos compartilhados com a tela de Conexões)
      ...controlesConexao()
    });

    this.aplicarValidadores();
  }

  // Conexão e credencial são gerenciadas na tela de Conexões; na edição só o Provedor é salvo
  private configurarModoEdicao(id: string): void {
    this.isEditMode = true;
    this.provedorId = id;
    CAMPOS_CONEXAO.forEach(campo => this.form.get(campo)?.disable({ emitEvent: false }));
    // Trocar o tipo invalidaria a configuração das conexões já cadastradas
    this.form.get('tipo')?.disable({ emitEvent: false });
    this.carregarProvedor(id);
  }

  carregarProvedor(id: string): void {
    this.loading = true;
    this.smartintegService.buscarProvedor(id).subscribe({
      next: (provedor: Provedor) => {
        this.provedorCarregado = provedor;
        this.form.patchValue({
          nome: provedor.nome,
          descricao: provedor.descricao,
          tipo: provedor.tipo
        }, { emitEvent: false });
        this.loading = false;
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao carregar provedor: ' + mensagemErroApi(error));
        this.loading = false;
        this.voltar();
      }
    });
  }

  private aplicarValidadores(): void {
    aplicarValidadoresConexao(this.form, this.tipoProvedor);
  }

  salvar(): void {
    if (this.form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios');
      this.marcarCamposComoTocados();
      return;
    }

    this.loading = true;

    if (this.isEditMode) {
      this.atualizar();
    } else {
      this.criar();
    }
  }

  criar(): void {
    const v = this.form.getRawValue();
    const api = v.tipo === TipoProvedor.APIREST;

    const data: CreateProvedorCompleto = {
      nome: v.nome,
      descricao: v.descricao || undefined,
      tipo: v.tipo,
      nomeConexao: v.nomeConexao,
      ambiente: v.ambiente,
      urlBase: api ? v.urlBase : undefined,
      padrao: v.padrao,
      config: montarConfig(v, v.tipo) as CreateProvedorCompleto['config'],
      tipoCredencial: v.tipoCredencial,
      credenciais: montarCredenciais(v)
    };

    this.smartintegService.criarProvedorCompleto(data).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Provedor criado com sucesso');
        this.loading = false;
        this.voltar();
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao criar provedor: ' + mensagemErroApi(error));
        this.loading = false;
      }
    });
  }

  atualizar(): void {
    const v = this.form.getRawValue();

    const data: Provedor = {
      ...this.provedorCarregado!,
      id: this.provedorId!,
      nome: v.nome,
      descricao: v.descricao,
      tipo: v.tipo
    };

    this.smartintegService.atualizarProvedor(data).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Provedor atualizado com sucesso');
        this.loading = false;
        this.voltar();
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao atualizar provedor: ' + mensagemErroApi(error));
        this.loading = false;
      }
    });
  }

  gerenciarConexoes(): void {
    this.router.navigate(['/smartinteg/provedores', this.provedorId, 'conexoes']);
  }

  marcarCamposComoTocados(): void {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });
  }

  campoInvalido(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control && control.invalid && control.touched);
  }

  voltar(): void {
    this.router.navigate(['/smartinteg/provedores']);
  }

  limpar(): void {
    this.form.reset({
      nome: '',
      descricao: '',
      tipo: TipoProvedor.BASEDADOS,
      ...valoresPadraoConexao(TipoProvedor.BASEDADOS)
    });
    this.aplicarValidadores();
  }
}
